import { IReparceling } from 'modules/customer/domain/entities';
import {
  PAYMENT_METHOD_TO_DOMAIN,
  TVectisPaymentMethodType,
} from 'shared/infra/adapters';

export interface IVectisReparceling {
  hash: string;
  valido: boolean;
  idCliente: number;
  nomeCliente: string;
  codigoContrato: number;
  idContrato: number;
  descricaoItensContrato: string[];
  percentualMinimoEntrada: number;
  valorMinimoEntrada: number;
  valorPendente: number;
  valorFuturo: number;
  valorTotalReparcelamento: number;
  formasPagamentoDisponivel: TVectisPaymentMethodType[];
  expirada?: boolean;
  dataValidade?: string;
  parcelas: Array<{
    numeroParcela: number;
    valor: number;
    valorFinal: number;
    valorMulta: number;
    data: string;
  }>;
}

const VECTIS_DATE_TIME = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/;

function parseVectisDateTime(value?: string) {
  if (!value) return undefined;

  const match = VECTIS_DATE_TIME.exec(value);

  if (!match) return undefined;

  const [, day, month, year, hours, minutes] = match;

  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
  );

  const isWithinRange =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day) &&
    date.getHours() === Number(hours) &&
    date.getMinutes() === Number(minutes);

  return isWithinRange ? date : undefined;
}

export class Reparceling implements IReparceling {
  public hash: IReparceling['hash'];
  public valid: IReparceling['valid'];
  public expired: IReparceling['expired'];
  public expiresAt: IReparceling['expiresAt'];
  public customer: IReparceling['customer'];
  public contract: IReparceling['contract'];
  public cashMinimum: IReparceling['cashMinimum'];
  public installments: IReparceling['installments'];
  public paymentMethods: IReparceling['paymentMethods'];

  constructor({
    hash,
    valido,
    idCliente,
    nomeCliente,
    codigoContrato,
    idContrato,
    descricaoItensContrato,
    percentualMinimoEntrada,
    valorMinimoEntrada,
    valorFuturo,
    valorPendente,
    valorTotalReparcelamento,
    formasPagamentoDisponivel,
    expirada,
    dataValidade,
    parcelas,
  }: IVectisReparceling) {
    this.hash = hash;
    this.valid = valido;
    this.expired = Boolean(expirada);
    this.expiresAt = parseVectisDateTime(dataValidade);
    this.customer = {
      id: idCliente,
      name: nomeCliente,
    };
    this.contract = {
      code: codigoContrato,
      id: idContrato,
      areas: descricaoItensContrato,
    };
    this.cashMinimum = {
      calculationPercentage: percentualMinimoEntrada,
      value: valorMinimoEntrada,
    };
    this.installments = {
      total: valorTotalReparcelamento,
      next: valorFuturo,
      pending: valorPendente,
      list: parcelas.map(installment => ({
        date: installment.data,
        fine: { value: installment.valorMulta },
        number: installment.numeroParcela,
        value: {
          initial: installment.valor,
          withInterest: installment.valorFinal,
        },
        hasFineOrInterest: installment.valor !== installment.valorFinal,
      })),
    };
    this.paymentMethods = formasPagamentoDisponivel
      .filter(method => method in PAYMENT_METHOD_TO_DOMAIN)
      .map(method => PAYMENT_METHOD_TO_DOMAIN[method]);
  }
}
